import { Route } from '@angular/router';

import { union } from '@ngrx/store';

import { CommonBookingFeatureState } from '@fcom/common-booking';
import { GlobalActions } from '@fcom/core/actions';
import { updateStoreAction } from '@fcom/core/store/init-store.action';
import { SeatCharacteristics } from '@fcom/dapi';
import { Category, FinnairPassengerServiceItem, FinnairServiceItem } from '@fcom/dapi/api/models';
import { FlightSeatSelections, SeatMapState, SeatMapStatus } from '@fcom/dapi/interfaces';
import { compareAsJson, DataUtils, deleteIn } from '@fcom/utils';
import { GlobalBookingActions } from '@fcom/common/store';
import { BookingActions, CartActions, OffersActions } from '@fcom/booking/store/actions';
import { BookingAppState } from '@fcom/booking/interfaces';
import { RootPaths } from '@fcom/core/constants';

import { OrderActions, SeatMapActions } from '../actions';
import { combineIncludedAndUnpaidSeats } from '../../modules/ancillaries/utils';

export const initialState: SeatMapState = Object.seal({});

const flightServices = (service: FinnairServiceItem | undefined, flightId: string) => {
  if (!service) {
    return [];
  }
  return [
    ...service.bounds.reduce(
      (allServices, b) => allServices.concat(...(b.segments.find((s) => s.id === flightId)?.passengers || [])),
      []
    ),
  ];
};

const toFlightSeatSelections = (
  seatSelectionsInCart: FinnairServiceItem,
  flightId: string,
  includedSeats: FinnairPassengerServiceItem[],
  setPaidStatus: boolean
): FlightSeatSelections =>
  flightServices(seatSelectionsInCart, flightId).reduce(
    (acc: FlightSeatSelections, service: FinnairPassengerServiceItem) => {
      const seatService = service.services.find((s) => !!s.seatNumber) || service.services[0];
      acc[service.id] = {
        seatNumber: seatService.seatNumber,
        seatType: seatService.seatType,
        characteristics: seatService.characteristics as SeatCharacteristics[],
        paid: setPaidStatus
          ? includedSeats.some((includedSeat) => compareAsJson(includedSeat, service)) &&
            parseFloat(seatService.totalPrice?.amount) > 0
          : false,
        modifiable: seatService.modifiable ?? true,
        ticketed: seatService.ticketed ?? false,
        travelerId: service.id,
        pricePerItem: seatService.unitPrice,
      };
      return acc;
    },
    {}
  );

const loadSeatSelections = function (
  state: SeatMapState,
  cartSeatSelections: FinnairServiceItem | undefined,
  includedSeats: FinnairServiceItem[],
  setPaidStatus: boolean
) {
  const clearedSelections = Object.keys(state).reduce((s: SeatMapState, currentFlightId: string) => {
    return deleteIn(s, [currentFlightId, 'selections']);
  }, state);
  return (
    cartSeatSelections?.bounds.reduce((s: SeatMapState, currentBound) => {
      return currentBound.segments.reduce((s2: SeatMapState, currentSegment) => {
        const newSelections = toFlightSeatSelections(
          cartSeatSelections,
          currentSegment.id,
          includedSeats.flatMap((service) =>
            service.bounds
              .filter((bound) => bound.id === currentBound.id)
              .flatMap((bound) =>
                bound.segments
                  .filter((segment) => segment.id === currentSegment.id)
                  .flatMap((segment) => segment.passengers)
              )
          ),
          setPaidStatus
        );
        return (
          DataUtils.wrap(s2)
            // Set default value for seat map status
            .updateIn([currentSegment.id, 'status'], (status: SeatMapStatus) => status || SeatMapStatus.INITIAL)
            .setIn([currentSegment.id, 'selections'], newSelections)
            .value()
        );
      }, s);
    }, clearedSelections) || clearedSelections
  );
};

const all = union({
  setOrderData: OrderActions.setOrderData,
  updateStoreAction,
  setCartData: CartActions.setCartData,
  setLocations: BookingActions.setLocations,
  setPaxAmount: GlobalBookingActions.setPaxAmount,
  increasePaxAmountField: GlobalBookingActions.increasePaxAmountField,
  decreasePaxAmountField: GlobalBookingActions.decreasePaxAmountField,
  setTravelType: BookingActions.setTravelType,
  selectTravelClass: BookingActions.selectTravelClass,
  confirmation: GlobalActions.confirmation,
  reset: OffersActions.reset,
  setInboundId: OffersActions.setInboundId,
  setInboundFareFamily: OffersActions.setInboundFareFamily,
  setOutboundId: OffersActions.setOutboundId,
  setOutboundFareFamily: OffersActions.setOutboundFareFamily,
  setDates: GlobalBookingActions.setDates,
});

type ExtraActions = typeof all;

function isCart(payload: BookingAppState | CommonBookingFeatureState): payload is BookingAppState {
  return !!(payload as BookingAppState)?.cart?.cartData;
}

function getServices(payload: BookingAppState | CommonBookingFeatureState): FinnairServiceItem[] {
  return isCart(payload)
    ? payload?.cart?.cartData?.services.unpaid
    : combineIncludedAndUnpaidSeats(
        payload?.commonBooking?.order?.orderData?.services?.included || [],
        payload?.commonBooking?.order?.orderData?.services?.unpaid || []
      );
}

// eslint-disable-next-line complexity
export function seatMapReducer(state = initialState, action: SeatMapActions.ActionsUnion | ExtraActions): SeatMapState {
  /* eslint-disable no-fallthrough */
  switch (action.type) {
    case OrderActions.setOrderData.type: {
      // TODO: Should use UX model instead of backend model
      const services: FinnairServiceItem[] = combineIncludedAndUnpaidSeats(
        action.order?.services?.included ?? [],
        action.order?.services?.unpaid ?? []
      );
      return loadSeatSelections(
        initialState,
        services?.find((s) => s.category === Category.SEAT),
        (action.order?.services?.included ?? []).filter((s) => s.category === Category.SEAT),
        true
      );
    }
    case updateStoreAction.type: {
      const services: FinnairServiceItem[] = getServices(action.payload);
      return loadSeatSelections(
        state,
        services?.find((s) => s.category === Category.SEAT),
        !isCart(action.payload)
          ? (
              (action.payload as CommonBookingFeatureState)?.commonBooking?.order?.orderData?.services?.included ?? []
            ).filter((s) => s.category === Category.SEAT)
          : [],
        !isCart(action.payload)
      );
    }
    case SeatMapActions.startLoading.type:
      return DataUtils.wrap(state).mergeIn(action.flightId, { status: SeatMapStatus.PENDING }).value();
    case SeatMapActions.setNoAvailability.type:
      return DataUtils.wrap(state).mergeIn(action.flightId, { status: SeatMapStatus.OK_NO_AVAILABILITY }).value();
    case SeatMapActions.error.type:
      return DataUtils.wrap(state).setIn(action.flightId, { status: SeatMapStatus.LOAD_ERROR }).value();
    case SeatMapActions.setSeatForFlight.type: {
      return DataUtils.wrap(state)
        .updateIn([action.flightId, 'selections'], (selections: FlightSeatSelections) => {
          if (!selections) {
            return {};
          }
          const conflictingTravelerIds: string[] = Object.keys(selections)
            .filter((travelerId) => travelerId !== action.travelerId)
            .filter((travelerId) => !!selections[travelerId])
            .filter((travelerId) => selections[travelerId].seatNumber === action.seat?.seatNumber);

          return conflictingTravelerIds.reduce(
            (_acc, conflictingId) =>
              DataUtils.wrap(selections)
                .setIn([conflictingId, 'seatNumber'], null)
                .setIn([conflictingId, 'seatType'], null)
                .setIn([conflictingId, 'characteristics'], null)
                .value(),
            selections
          );
        })
        .setIn([action.flightId, 'selections', action.travelerId, 'seatNumber'], action.seat?.seatNumber || null)
        .setIn([action.flightId, 'selections', action.travelerId, 'seatType'], action.seat?.type || null)
        .setIn(
          [action.flightId, 'selections', action.travelerId, 'characteristics'],
          (action.seat?.characteristics as SeatCharacteristics[]) || null
        )
        .setIn([action.flightId, 'selections', action.travelerId, 'travelerId'], action.travelerId)
        .setIn([action.flightId, 'selections', action.travelerId, 'paid'], false)
        .setIn([action.flightId, 'selections', action.travelerId, 'ticketed'], false)
        .setIn([action.flightId, 'selections', action.travelerId, 'modifiable'], true)
        .value();
    }
    case SeatMapActions.setSeatMap.type:
      return DataUtils.wrap(state)
        .mergeIn(action.seatMapForFlight.flightId, {
          status: SeatMapStatus.OK,
          seatMap: action.seatMapForFlight.seatMap,
        })
        .value();

    case SeatMapActions.loadSeatSelectionsFromCart.type:
    case CartActions.setCartData.type:
      return loadSeatSelections(
        state,
        action.cartData?.services.unpaid?.find((s) => s.category === Category.SEAT),
        [],
        false
      );

    case SeatMapActions.reset.type:
    case SeatMapActions.clearSelections.type:

    // Data Clean-ups: BookingActions
    case BookingActions.setLocations.type:
    case BookingActions.setTravelType.type: // @todo Do we need to fix this, as we moved setTravelClass from booking to the globalBooking store?
    case BookingActions.selectTravelClass.type: // @todo Do we need to fix this, as we moved selectTravelClass from booking to the globalBooking store?

    // Data Clean-ups: GlobalBookingActions
    case GlobalBookingActions.setDates.type:
    case GlobalBookingActions.setPaxAmount.type:
    case GlobalBookingActions.increasePaxAmountField.type:
    case GlobalBookingActions.decreasePaxAmountField.type:

    // Data Clean-ups: When order created
    case GlobalActions.confirmation.type: {
      if ((action as { route?: Route }).route?.path === RootPaths.MANAGE_BOOKING_ROOT) {
        return state;
      }
      return initialState;
    }

    // Data Clean-ups: OffersActions
    case OffersActions.reset.type:
    case OffersActions.setInboundId.type:
    case OffersActions.setInboundFareFamily.type:
    case OffersActions.setOutboundId.type:
    case OffersActions.setOutboundFareFamily.type:
      return initialState;
    default:
      return state;
  }
  /* eslint-enable no-fallthrough */
}
